import { API_BASE_URL } from './api';

// Minimal real-time client for the notification bell's
// apps.notifications.consumers.NotificationConsumer (ws/notifications/ on
// the backend) — same shape as trackingSocket.js (persistent socket, own
// reconnect backoff, same sessionStorage access-token convention as api.js).
//
// The 90s polling in AppLayout stays in place as the reconciliation
// fallback while disconnected, exactly like EmployeeTracking does for
// trackingSocket — this client only makes updates arrive sooner, it isn't
// the source of truth.

const RECONNECT_MIN_MS = 2000;
const RECONNECT_MAX_MS = 30000;

function wsUrl() {
    const httpUrl = new URL(`${API_BASE_URL}/ws/notifications/`);
    const scheme = httpUrl.protocol === 'https:' ? 'wss:' : 'ws:';
    const token = sessionStorage.getItem('access_token') || '';
    return `${scheme}//${httpUrl.host}/ws/notifications/?token=${encodeURIComponent(token)}`;
}

export function connectNotificationSocket({ onMessage, onStatusChange }) {
    let socket = null;
    let closedByCaller = false;
    let reconnectDelay = RECONNECT_MIN_MS;
    let reconnectTimer = null;

    const notifyStatus = (status) => {
        if (onStatusChange) onStatusChange(status);
    };

    const open = () => {
        if (closedByCaller) return;
        try {
            socket = new WebSocket(wsUrl());
        } catch {
            scheduleReconnect();
            return;
        }

        socket.onopen = () => {
            reconnectDelay = RECONNECT_MIN_MS;
            notifyStatus('connected');
        };

        socket.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                if (data.type === 'connected') return;
                if (onMessage) onMessage(data);
            } catch {
                // Ignore malformed frames — never crash the page over a bad message.
            }
        };

        socket.onclose = () => {
            notifyStatus('disconnected');
            scheduleReconnect();
        };

        socket.onerror = () => {
            // onclose fires right after onerror for a WebSocket — reconnect is
            // scheduled there, nothing extra to do here besides not throwing.
        };
    };

    const scheduleReconnect = () => {
        if (closedByCaller || reconnectTimer) return;
        reconnectTimer = setTimeout(() => {
            reconnectTimer = null;
            reconnectDelay = Math.min(reconnectDelay * 2, RECONNECT_MAX_MS);
            open();
        }, reconnectDelay);
    };

    open();

    return function disconnect() {
        closedByCaller = true;
        if (reconnectTimer) clearTimeout(reconnectTimer);
        if (socket) {
            socket.onclose = null;
            socket.close();
        }
    };
}
